import {
  MRT_ColumnDef,
  MRT_ColumnOrderState,
  MRT_VisibilityState
} from 'material-react-table';
import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { EventSearchDocument } from 'types/Search';

export const useSearchTableColumnManagement = (
  /**
   * The unique identifier for the table. For brand portal, it's the event key; for marketplace, it's marketplace_type_segment
   */
  identifier: string,
  columnConfigColumns:
    | {
        id: string;
      }[]
    | undefined,

  columns: MRT_ColumnDef<EventSearchDocument>[] | undefined
) => {
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {}
  );
  const { isAuthenticated } = useAuth();
  const [columnOrder, setColumnOrder] = useState<MRT_ColumnOrderState>(() => {
    // Load the saved column order from localStorage on initialization
    const savedOrder = localStorage.getItem(`columnOrder_${identifier}`);
    return savedOrder
      ? JSON.parse(savedOrder)
      : [
          'mrt-row-select',
          'bookmarked',
          ...(columnConfigColumns?.map(el => el.id) ?? []),
          'mrt-row-actions'
        ];
  });
  useEffect(() => {
    // Save the column order to localStorage whenever it changes
    isAuthenticated &&
      localStorage.setItem(
        `columnOrder_${identifier}`,
        JSON.stringify(columnOrder)
      );
  }, [columnOrder, identifier, isAuthenticated]);

  useEffect(() => {
    // we dont want to store the initial object visibility value which is an empty object
    if (Object.keys(columnVisibility).length !== 0 && isAuthenticated)
      localStorage.setItem(
        `columnVisibility_${identifier}`,
        JSON.stringify(columnVisibility)
      );
  }, [columnVisibility, identifier, isAuthenticated]);

  useEffect(() => {
    // We only want to set column visibility once when columns are initially loaded.
    //   but we don't want to overwrite it every time the columns object changes
    if (Object.keys(columnVisibility).length === 0 && columns) {
      const savedVisibility = localStorage.getItem(
        `columnVisibility_${identifier}`
      );
      // Check if we have saved visibility in localStorage
      if (savedVisibility) {
        setColumnVisibility(JSON.parse(savedVisibility));
      } else {
        // No saved visibility, set default visibility based on columnConfigColumns
        const defaultVisibility = {
          ...Object.fromEntries(
            columns.map(col => [
              col.accessorKey,
              columnConfigColumns?.some(el => el.id === col.accessorKey) ?? true
            ])
          ),
          'mrt-row-actions': true,
          'mrt-row-selection': true,
          bookmarked: true
        };
        setColumnVisibility(defaultVisibility);
      }
    }
  }, [identifier, columns, columnVisibility, columnConfigColumns]);

  return { columnOrder, setColumnOrder, columnVisibility, setColumnVisibility };
};
